import { Type } from '@angular/core';

import { cloneDeep } from 'lodash';

import { FilterOptionModel } from './filter-option.model';
import { ChipModel } from '../../Forms/models/chip.model';

import { SelectedMediaTypesFilterModel } from '../../../Monitor/Shared/models/selected-media-types-filter.model';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';

import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { CustomFilterTypeModel } from './custom-filter-type.model';
import { AbstractFilterTypeComponent } from './abstract-filter-type.component';
import { FilterQuickOptionModel } from './filter-quick-option.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { monitoringFiltersEnum } from '../../../Monitor/Shared/enums/monitoring-filters.enum';
import { IncludeExcludeEnum } from '../enums/include-exclude.enum';


export const ARCHIVED_BROADCAST_ONLY_CHIP_MODEL = {
    label: 'Monitoring.filtersLocalization.archivedBroadcast'
};

export const mediaTypesWithSubTypesMap:  Map<MediaTypeEnum, MediaTypeModel[]> = new Map([
    [ MediaTypeEnum.TWITTER,  MediaTypeModel.getAllTwitterSubTypes() ],
    [ MediaTypeEnum.LINKEDIN, MediaTypeModel.getAllLinkedinSubTypes() ],
    [ MediaTypeEnum.BLUESKY,  MediaTypeModel.getAllBlueskySubTypes() ],
    [ MediaTypeEnum.TV,  MediaTypeModel.getTvSubTypes() ]
]);

/**
 * All child options will be contained in the "children" property of the parents MediaTypeModel
*/
export class MediaTypeTreeFilterModel<T = MediaTypeModel> extends CustomFilterTypeModel<SelectedMediaTypesFilterModel> {

    options: FilterOptionModel<MediaTypeModel>[];
    showSearch: boolean;
    allowArchivedBroadcastFilter: boolean;
    isSocial: boolean;

    protected value: SelectedMediaTypesFilterModel;
    protected currentValue: SelectedMediaTypesFilterModel = new SelectedMediaTypesFilterModel([], false);

    readonly id = monitoringFiltersEnum.mediaTypes;

    constructor(
        id: string,
        label: string,
        component: Type<AbstractFilterTypeComponent>,
        private localizationService: LocalizationService,
        options?: FilterOptionModel<MediaTypeModel>[],
        value?: SelectedMediaTypesFilterModel,
        section?: string,
        isSocial: boolean = false,
        showIncludeExclude?: boolean
    ) {
        super(id, label, component, section, showIncludeExclude);

        this.options = options || [];
        this.setValue(value || new SelectedMediaTypesFilterModel([], false));
        this.isSocial = isSocial;
    }

    private constructChips(value: SelectedMediaTypesFilterModel): ChipModel[] {

        const chips: ChipModel[] = [];

        if (this.options) {

            // if selected mediaTypes match options, add chip
            value.mediaTypes.forEach((mediaType) => {
                if (!mediaTypesWithSubTypesMap.has(mediaType.id)) {
                    chips.push(cloneDeep(this.options.find(opt => opt.value.id === mediaType.id)));
                } else {
                    // handle special case of media with sub options
                    const parent = mediaType;
                    const parentOpt = cloneDeep(this.options.find(opt => mediaType.id === opt.value.id));

                    if (parent?.children && parentOpt) {
                        const newChildren = parent?.children;

                        parentOpt.label = `${this.localizationService.transform(parent.name)}` + (newChildren?.length ? ': ' : '');
                        if (mediaTypesWithSubTypesMap.get(parent.id).length === newChildren.length) {
                            parentOpt.label = parentOpt.label + this.localizationService.transform('Social.filter.includeAll');
                        } else {
                            newChildren.forEach((child, index) => {
                                const label = (index !== 0) ? ', ' : '';
                                parentOpt.label = parentOpt.label + label + this.localizationService.transform(child.value.name);
                            });
                        }

                        chips.push(parentOpt);
                    } else {
                        // sometimes parent doesn't have children defined (ex when you drilldown in reports on a mediaType)
                        chips.push(cloneDeep(this.options.find(opt => opt.value.id === mediaType.id)));
                    }
                }

            });
        }


        if (value.archivedOnly) {
            chips.push(ARCHIVED_BROADCAST_ONLY_CHIP_MODEL);
        }

        return chips.filter(chip => !!chip);
    }

    /** Sets the active value for this filter */
    setValue(value: SelectedMediaTypesFilterModel) {
        this.value = value;
        this.chips = [];
        const chips = this.constructChips(this.value);
        if (chips) {
            this.chips.push(...chips);
        }
        this.cancelChanges();
        this.updateChipSuffix();
    }

    /** Gets the active value for this filter */
    getValue(): SelectedMediaTypesFilterModel {
        return this.value;
    }

    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    removeChip(chip: ChipModel) {
        this.value.mediaTypes = this.value.mediaTypes.filter(mediaType => {
            if (mediaTypesWithSubTypesMap.has(mediaType.id) && mediaTypesWithSubTypesMap.has((<any>chip).value?.id)) {
                return !(mediaType.id === (<any>chip).value.id);
            }
            return !mediaType.equals((<any>chip).value);
        });

        const broadcastMediaSelected = this.value.mediaTypes.find(mediaType => {
            if (
                mediaType.id === MediaTypeEnum.TV
                || mediaType.id === MediaTypeEnum.RADIO
            ) {
                return true;
            }

            return false;
        });

        if (!broadcastMediaSelected || chip === ARCHIVED_BROADCAST_ONLY_CHIP_MODEL) {
            this.value.archivedOnly = false;
        }

        this.setValue(this.value);
    }

    /**
     * Resets the filter to not apply any changes
     */
    clear() {
        this.setValue(new SelectedMediaTypesFilterModel([], false));
        this.setIncludeExclude(IncludeExcludeEnum.include);
    }

    /**
     * Sets the current value for a filter
     * @param value
     */
    setCurrentValue(value: SelectedMediaTypesFilterModel) {
        this.currentValue = value;
        this.currentChips = [];
        const chips = this.constructChips(this.currentValue);
        if (chips) {
            this.currentChips.push(...chips);
        }
        this.updateCurrentChipSuffix();
    }

    /**
     * Gets the current value for a filter
     */
    getCurrentValue(): SelectedMediaTypesFilterModel {
        return this.currentValue;
    }

    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    removeCurrentChip(chip: ChipModel): void {
        this.currentValue.mediaTypes = this.currentValue.mediaTypes.filter(mediaType => {
            if (mediaTypesWithSubTypesMap.has(mediaType.id) && mediaTypesWithSubTypesMap.has((<any>chip).value?.id)) {
                return !(mediaType.id === (<any>chip).value.id);
            }
            return !mediaType.equals((<any>chip).value);
        });

        const broadcastMediaSelected = this.currentValue.mediaTypes.find(mediaType => {
            if (
                mediaType.id === MediaTypeEnum.TV
                || mediaType.id === MediaTypeEnum.RADIO
            ) {
                return true;
            }

            return false;
        });

        if (!broadcastMediaSelected || chip === ARCHIVED_BROADCAST_ONLY_CHIP_MODEL) {
            this.currentValue.archivedOnly = false;
        }

        this.setCurrentValue(this.currentValue);
    }

    /**
     * Resets the current value to the active value
     */
    cancelChanges(): void {
        this.setCurrentValue(this.value.clone());
    }

    /**
     * Sets allowArchivedBroadcastFilter and updates value
     */
    setAllowArchivedBroadcastFilter(allowArchivedBroadcastFilter: boolean): void {
        if (!allowArchivedBroadcastFilter) {
            this.value.archivedOnly = false;
            this.setValue(this.value);
        }
        this.allowArchivedBroadcastFilter = allowArchivedBroadcastFilter;
    }

    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel<MediaTypeModel>[] {
        const queryLower = query.toLocaleLowerCase();
        const matchedOptions = this.options.filter(
            opt => {
                if (this.value.mediaTypes.find(mediaType => mediaType.id === opt.value.id)) {
                    return false;
                }
                return translateMethod(opt.label).toLocaleLowerCase().includes(queryLower);
            }
        );

        return matchedOptions.map(opt => {
            return {
                label: opt.label,
                value: opt.value,
                filter: this
            };
        });
    }

    addQuickOption(option: FilterQuickOptionModel<MediaTypeModel>) {
        const val = this.getValue().clone();
        val.mediaTypes.push(option.value);
        this.setValue(val);
    }
}
